import React from 'react';

import styled from 'styled-components';

import { useAutoFilters } from '../hooks/useAutofilters';
import useGridInputs from '../hooks/useGridInputs';
import useInGridAssets from '../hooks/useInGridAssets';
import useProductGridData from '../hooks/useProductGridData';
import { getGridLayoutTrackingTags } from '../utils/tracking';

import DesktopGridFilters from './DesktopGridFilters/DesktopGridFilters';
import DesktopGridHeader from './DesktopGridHeader';
import DesktopGridVirtualScroll from './DesktopGridVirtualScroll';
import DesktopSideNav from './DesktopSideNav/DesktopSideNav';
import DesktopGridDiscoveryGuide from './DiscoveryGuide/DesktopGridDiscoveryGuide';
import GridLayoutTrackingProvider from './GridLayoutTrackingProvider';

const LayoutContainer = styled.div`
  width: 100%;
  max-width: 1504px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 42px;
  padding-bottom: 42px;
`;

const InnerContainer = styled.div`
  max-width: 1330px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;

const GridContentWrapper = styled.div`
  display: flex;
  padding: 0 2rem 2rem 2rem;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 268px;
  margin-right: 2rem;
`;

const DesktopGridLayout = () => {
  const {
    products,
    totalNumResults,
    fetchNextPage,
    hasNextPage,
    isCioBrowseFetching,
    isCioBrowseLoading,
    cioFacets,
    cioFilterName,
    cioFilterValue,
  } = useProductGridData();

  const { mergedAssetsAndProducts } = useInGridAssets({ products });
  const { builderState, gridGender } = useGridInputs();

  const { autofilterApplied } = useAutoFilters({
    builderState,
    gridGender,
    cioFacets,
  });

  const { trackingTags } = getGridLayoutTrackingTags({
    totalNumResults,
    cioFilterName,
    cioFilterValue,
  });

  return (
    <GridLayoutTrackingProvider>
      <LayoutContainer {...trackingTags}>
        <InnerContainer>
          <DesktopGridDiscoveryGuide />
          <DesktopGridHeader />
          <GridContentWrapper>
            <SideColumn>
              <DesktopSideNav />
              {/* To prevent complexity in determining state behavior between the following:
                    1. initial render (before autofilters are applied)
                    2. autofilters applied (after initial render)
                    3. user interaction (after autofilters are applied)
                  Treat the state after autofilters have been applied as the initial render. 
                  We don't need to do this for the mobile version due to the batched update behavior and 
                  useEffect firing on router change in useSelectedFacetsToApply() */}
              {autofilterApplied && <DesktopGridFilters />}
            </SideColumn>
            <DesktopGridVirtualScroll
              virtualList={mergedAssetsAndProducts}
              totalNumResults={totalNumResults}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isCioBrowseLoading={isCioBrowseLoading}
              isCioBrowseFetching={isCioBrowseFetching}
            />
          </GridContentWrapper>
        </InnerContainer>
      </LayoutContainer>
    </GridLayoutTrackingProvider>
  );
};

export default DesktopGridLayout;
